import React, { useEffect, useState } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import Select from "react-select";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import { customTableStyles } from "../../assets/CustomTableStyles";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function FeePlan() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");

  const [isLoading, setIsLoading] = useState(true);
  const [triggerRerender, setTriggerRerender] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      id: 0,
      name: "",
      registrationFee: "",
      registrationFeeVAT: null,
      sessionId: "",
      feeCycleId: "",
      createdBy: localStorage.getItem("userId"),
      organizationId: localStorage.getItem("organizationId"),
      feePlanDetail: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "feePlanDetail",
  });

  const [feeTypes, setFeeTypes] = useState([]);
  const [sessionsData, setSessionsData] = useState([]);
  const [feeCycles, setFeeCycles] = useState([]);
  const [feePlans, setFeePlans] = useState([]);

  // Fetch Fee Plans
  useEffect(() => {
    const fetchFeePlans = async () => {
      try {
        const response = await get(
          `FeePlan/GetAllFeePlanByOrganizationIdAsync?organizationId=${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFeePlans(response);
      } catch (error) {
        console.error("Error fetching feePlans:", error);
      }
    };
    fetchFeePlans();
  }, [triggerRerender]);

  // Fetch Fee Types
  useEffect(() => {
    const fetchFeeTypes = async () => {
      try {
        const response = await get(
          `FeeType/GetFeeTypeByOrganizationIdAsync?organizationId=${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setFeeTypes(response.feeType);

        const feePlanDetailArray = response.feeType.map((feeType) => ({
          feeTypeId: feeType.id || "",
          amount: "",
          vatPercentage: "",
          vatAfterAmount: "",
          remarks: "",
          createdBy: localStorage.getItem("userId"),
        }));

        setValue("feePlanDetail", feePlanDetailArray);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching feeType:", error);
      }
    };
    fetchFeeTypes();
  }, [triggerRerender]);

  // Fetch Sessions
  useEffect(() => {
    const getSessionsList = async () => {
      if (!token) return;
      try {
        const response = await get(
          `Session/GetSessions?organizationId=${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSessionsData(response.sessions);
      } catch (error) {
        console.error("Error fetching sessions:", error);
      }
    };
    getSessionsList();
  }, []);

  // Fetch Fee Cycles
  useEffect(() => {
    const fetchFeeCycles = async () => {
      try {
        const response = await get(
          `FeeCycle/GetFeeCycleByOrganizationIdAsync?organizationId=${organizationId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFeeCycles(response.feeCycle);
      } catch (error) {
        console.error("Error fetching feeCycle:", error);
      }
    };
    fetchFeeCycles();
  }, [triggerRerender]);

  // Handle Edit Click
  const handleEditClick = async (rowId) => {
    const singleFeePlan = await get(
      `FeePlan/GetFeePlanByIdAsync?feePlanId=${rowId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    const selectedSession = sessionsData.find(
      (session) => session.id === singleFeePlan.sessionId
    );
    const selectedFeeCycle = feeCycles.find(
      (feeCycle) => feeCycle.id === singleFeePlan.feeCycleId
    );

    const feePlanDetailMap = singleFeePlan.feePlanDetail.reduce(
      (map, detail) => {
        map[detail.feeTypeId] = detail;
        return map;
      },
      {}
    );

    const mappedFeePlanDetails = feeTypes.map((feeType) => {
      const detail = feePlanDetailMap[feeType.id] || {};
      const amount = parseFloat(detail.amount || 0);
      const vat = parseFloat(detail.vatPercentage || 0);
      const vatAfterAmount = amount + (amount * vat) / 100;

      return {
        feeTypeId: feeType.id,
        amount: amount || "",
        vatPercentage: vat || "",
        vatAfterAmount: vatAfterAmount || "",
        remarks: detail.remarks || "",
        createdBy: detail.createdBy || localStorage.getItem("userId"),
        updatedBy: localStorage.getItem("userId"),
      };
    });

    reset({
      id: singleFeePlan.id || 0,
      name: singleFeePlan.name || "",
      registrationFee: singleFeePlan.registrationFee || "",
      registrationFeeVAT: singleFeePlan.registrationFeeVAT || null,
      sessionId: selectedSession
        ? { value: selectedSession.id, label: selectedSession.name }
        : null,
      feeCycleId: selectedFeeCycle
        ? { value: selectedFeeCycle.id, label: selectedFeeCycle.name }
        : null,
      createdBy: singleFeePlan.createdBy || localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
      organizationId:
        singleFeePlan.organizationId || localStorage.getItem("organizationId"),
      isSaudi: singleFeePlan?.isSaudi || false,
      feePlanDetail: mappedFeePlanDetails,
    });

    remove();
    mappedFeePlanDetails.forEach((detail) => append(detail));
  };

  useEffect(() => {
    const amount = parseFloat(watch("registrationFee") || 0);
    const vat = parseFloat(watch("registrationFeeVAT") || 0);
    const total = amount + (amount * vat) / 100;
    setValue("vatAddedAssessmentFee", total.toFixed(2));
  }, [watch("registrationFee"), watch("registrationFeeVAT")]);

  const columns = [
    {
      accessorKey: "mrt-row-numbers",
      header: "Sr No.",
      enableColumnOrdering: false,
      enableEditing: false,
      enableSorting: false,
      size: 80,
      Cell: ({ row }) => row.index + 1,
    },
    { header: "Name", accessorKey: "name", size: 200 },
    {
      id: "actions",
      header: "Action",
      size: 120,
      Cell: ({ row }) => (
        <button
          type="button"
          className="btn p-0"
          onClick={() => handleEditClick(row.original.id)}
        >
          <i className="fas fa-pencil"></i>
        </button>
      ),
    },
  ];

  const exportToExcel = () => {
    const filteredData = feePlans.map((row, index) => ({
      "Sr No": index + 1,
      Name: row.name,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "FeePlans");
    XLSX.writeFile(workbook, "FeePlans.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;
    // doc.addImage(logo, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Fee Plans List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const headers = [["Sr No", "Name"]];

    const bodyData = feePlans.map((row, index) => [index + 1, row.name]);

    doc.autoTable({
      head: headers,
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("FeePlans.pdf");
  };

  const saveData = async (data) => {
    if (!token) return;

    const updatedFeePlanDetail = data.feePlanDetail.map((detail) => ({
      ...detail,
      amount: detail.amount || 0,
      vatPercentage: parseFloat(detail.vatPercentage || 0),
    }));

    const formData = {
      ...data,
      sessionId: data.sessionId?.value,
      feeCycleId: data.feeCycleId?.value,
      isSaudi: data.isSaudi ?? false,
      feePlanDetail: updatedFeePlanDetail,
    };

    try {
      const response = await post("FeePlan/AddUpdateFeePlanAsync", formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        setTriggerRerender((prev) => !prev);
        reset({
          name: "",
          registrationFee: "",
          registrationFeeVAT: null,
          centerSessionId: null,
          feeCycleId: null,
          isSaudi: false,
          feePlanDetail: feeTypes.map((feeType) => ({
            feeTypeId: feeType.id,
            amount: "",
            vatPercentage: "",
            vatAfterAmount: "",
            remarks: "",
            createdBy: localStorage.getItem("userId"),
          })),
        });
      }
    } catch (error) {
      console.error("Error saving fee plan:", error);
    }
  };

  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  if (isLoading) return <div className="fa fa-spinner fs-7"></div>;

  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading fs-7">Fee Plan</h2>
      </div>
      <div className="row g-3">
        <div className="col-md-7">
          <div className="card">
            <div className="card-body">
              <form
                className="new-form-style"
                onSubmit={handleSubmit(saveData)}
              >
                <div className="row g-3">
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Name<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("name", {
                        required: "This field is required.",
                        maxLength: { value: 75, message: "Max 75 characters." },
                      })}
                      type="text"
                      className="form-control"
                      onChange={handleChangeOfInputWithLengthCheck("name")}
                    />
                    {errors.name && (
                      <small className="text-danger">
                        {errors.name.message}
                      </small>
                    )}
                  </div>

                  {/* Session */}
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Academic Year / Session
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="sessionId"
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={sessionsData.map((s) => ({
                            value: s.id,
                            label: s.name,
                          }))}
                          onChange={(option) => {
                            setValue("sessionId", option);
                            trigger("sessionId");
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                        />
                      )}
                    />
                    {errors.sessionId && (
                      <small className="text-danger">
                        {errors.sessionId.message}
                      </small>
                    )}
                  </div>

                  {/* Fee Cycle */}
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Fee Cycle<span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="feeCycleId"
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={feeCycles.map((c) => ({
                            value: c.id,
                            label: c.name,
                          }))}
                          onChange={(option) => {
                            setValue("feeCycleId", option);
                            trigger("feeCycleId");
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                        />
                      )}
                    />
                    {errors.feeCycleId && (
                      <small className="text-danger">
                        {errors.feeCycleId.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Registration Fee
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-control"
                      {...register(`registrationFee`, {
                        valueAsNumber: true,
                        required: "This field is required",
                        validate: (value) =>
                          value >= 0 || "Please enter correct value.",
                      })}
                      onKeyDown={(e) => {
                        if (e.key === "e" || e.key === "+" || e.key === "-") {
                          e.preventDefault();
                        }
                      }}
                      onInput={(e) => {
                        const value = parseFloat(e.target.value);
                        if (isNaN(value) || value < 0) {
                          e.target.value = "";
                        }
                      }}
                    />
                    {errors.registrationFee && (
                      <small className="text-danger">
                        {errors.registrationFee.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Registration Fee VAT (%)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      {...register(`registrationFeeVAT`, {
                        valueAsNumber: true,
                      })}
                      onInput={(e) => {
                        const inputValue = e.target.value;
                        const vat =
                          inputValue === ""
                            ? null
                            : parseFloat(inputValue) || 0;
                        const amount =
                          parseFloat(watch("registrationFee")) || 0;

                        if (vat === null || vat === 0) {
                          setValue("vatAddedAssessmentFee", null); // null when VAT is empty
                        } else {
                          const vatAfterAmount = amount + (amount * vat) / 100;
                          setValue(
                            "vatAddedAssessmentFee",
                            vatAfterAmount.toFixed(2)
                          );
                        }
                      }}
                    />
                    {errors.registrationFeeVAT && (
                      <small className="text-danger">
                        {errors.registrationFeeVAT.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      VAT Added Registration Fee
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      readOnly
                      disabled
                      {...register("vatAddedAssessmentFee")}
                    />
                  </div>

                  {/* Is Saudi */}
                  <div className="col-md-4 d-none">
                    <div className="form-check">
                      <Controller
                        name="isSaudi"
                        control={control}
                        render={({ field }) => (
                          <input
                            id="isSaudi"
                            type="checkbox"
                            checked={field.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                            className="form-check-input"
                          />
                        )}
                      />
                      <label className="form-check-label" htmlFor="isSaudi">
                        Is Saudi
                      </label>
                    </div>
                  </div>

                  <div className="w-100 m-0"></div>

                  {/* Table */}
                  <div className="col-md-12">
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle text-center custom-table m-0">
                        <thead className="">
                          <tr>
                            <th className="text-start" style={{ width: "30%" }}>
                              Fee Type
                            </th>
                            <th>Fee Amount</th>
                            <th>VAT(%)</th>
                            <th>VAT Added Amount</th>
                            <th style={{ width: "15%" }}>Cycle</th>
                          </tr>
                        </thead>
                        <tbody>
                          {feeTypes?.map((feeType, index) => (
                            <tr key={index}>
                              <td className="text-start">{feeType.name}</td>

                              {/* Fee Amount */}
                              <td>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm text-end py-1"
                                  {...register(
                                    `feePlanDetail[${index}].amount`
                                  )}
                                  onInput={(e) => {
                                    const amount =
                                      parseFloat(e.target.value) || 0;
                                    const vat =
                                      parseFloat(
                                        watch(
                                          `feePlanDetail[${index}].vatPercentage`
                                        )
                                      ) || 0;
                                    const vatAfterAmount =
                                      amount + (amount * vat) / 100;
                                    setValue(
                                      `feePlanDetail[${index}].vatAfterAmount`,
                                      vatAfterAmount.toFixed(2)
                                    );
                                  }}
                                />
                              </td>

                              {/* VAT % */}
                              <td>
                                <input
                                  type="number"
                                  step="0.01"
                                  className="form-control form-control-sm text-end py-1"
                                  {...register(
                                    `feePlanDetail[${index}].vatPercentage`
                                  )}
                                  onInput={(e) => {
                                    const vat = parseFloat(e.target.value) || 0;
                                    const amount =
                                      parseFloat(
                                        watch(`feePlanDetail[${index}].amount`)
                                      ) || 0;
                                    const vatAfterAmount =
                                      amount + (amount * vat) / 100;
                                    setValue(
                                      `feePlanDetail[${index}].vatAfterAmount`,
                                      vatAfterAmount.toFixed(2)
                                    );
                                  }}
                                />
                              </td>

                              {/* VAT After Amount */}
                              <td>
                                <input
                                  type="number"
                                  step="0.01"
                                  readOnly
                                  className="form-control form-control-sm text-end bg-light py-1"
                                  {...register(
                                    `feePlanDetail[${index}].vatAfterAmount`
                                  )}
                                />
                              </td>

                              {/* Cycle */}
                              <td className="text-center text-nowrap">
                                {feeType.feeFrequency}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-12">
                    <button
                      className="btn btn-primary btn-sm"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* DataTable Section */}
        <div className="col-md-5">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={feePlans}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{ columnPinning: { right: ["actions"] } }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FeePlan;
